<template>
    <div class="admin-quiz-table">
        <v-data-table-server
            :items="items"
            :headers="headers"
            :loading="loading"
            :loading-text="$t('common.loading')"
            :no-data-text="$t('common.noDataText')"
            :items-per-page-text="`${$t('common.rowsPerPage')}`"
            :page-text="`${currentPage} ${$t('common.of')} ${lastPage}`"
            :items-per-page-options="itemsPerPageOptions"
            :items-length="total"
            :options="options"
            fixed-header
            :height="items.length > 10 ? '60vh' : '100%'"
            @update:options="optionsUpdate"
            @click:row="onRowClick"
        >
            <template #loading>
                <div class="admin-quiz-table__loading">{{ $t('common.loading') }}</div>
            </template>

            <template #top>
                <div class="kasanowa-top-filter-wrapper">
                    <KTextField
                        id="search"
                        v-model="search"
                        name="search"
                        label=""
                        :placeholder="$t('common.search')"
                        hide-details
                        clearable
                    />
                </div>
            </template>

            <template #item.name="elem">
                <div v-if="elem.item.name != null">{{ elem.item.name }}</div>
            </template>

            <template #item.created_at="elem">
                <div v-if="elem.item.created_at != null">{{ useDataFormat(elem.item.created_at) }}</div>
            </template>

            <template #item.date_from="elem">
                <div v-if="elem.item.start_at != null">{{ useDataFormat(elem.item.start_at) }}</div>
            </template>

            <template #item.date_to="elem">
                <div v-if="elem.item.end_at != null">{{ useDataFormat(elem.item.end_at) }}</div>
            </template>

            <template #item.price="elem">
                <div v-if="elem.item.value != null">{{ elem.item.value }}PLN</div>
                <div v-else>0.00 PLN</div>
            </template>

            <template #item.survey="elem">
                <div v-if="elem.item.is_questionnaire != null">{{ !!elem.item.is_questionnaire ? $t('quiz.yes') : $t('quiz.no') }}</div>
            </template>

            <template #item.answers="elem">
                <div v-if="elem.item.answers_count != null">{{ elem.item.answers_count }}</div>
                <div v-else>0</div>
            </template>

            <template #item.actions="elem">
                <div class="admin-quiz-table__action-wrapper" @click.stop>
                    <KTooltip without-btn>
                        <template v-slot:title>
                            <div class="kasanowa-hover admin-quiz-table__icon-wrapper admin-quiz-table__icon-wrapper--edit" @click="navigateTo(`/quiz/${elem.item.id}`)">
                                <KIcon icon="edit" :color="grey400" />
                            </div>
                        </template>
                        <template v-slot:content>
                            <div>{{ $t('quiz.edit') }}</div>
                        </template>
                    </KTooltip>
                    <KTooltip without-btn>
                        <template v-slot:title>
                            <div class="kasanowa-hover admin-quiz-table__icon-wrapper admin-quiz-table__icon-wrapper--trash" @click="openDeleteWarehouseDialog(elem.item.id)">
                                <KIcon icon="trash" :color="grey400" />
                            </div>
                        </template>
                        <template v-slot:content>
                            <div>{{ $t('quiz.delete') }}</div>
                        </template>
                    </KTooltip>
                </div>
            </template>

            <template #footer.prepend>
                <div class="kasanowa-total-table-counter">{{ $t('common.total') }}: {{ total }}</div>
            </template>
        </v-data-table-server>

        <KDialog
            :is-open-dialog-action="isOpenDeleteDialog"
            :is-close-dialog-action="isCloseDeleteDialog"
            close-header-btn
            @close-dialog="closeDialogWhenNoTargetPressed"
        >
            <template v-slot:default>
                <div>
                    <div class="admin-quiz-table__dialog-title">{{ $t('quiz.areYouSureDeleteQuiz') }}</div>
                    <div class="admin-quiz-table__btn-dialog-wrapper">
                        <KButton
                            id="quiz-cancel"
                            :label="$t('quiz.cancel')"
                            secondary
                            @click="closeDialogWhenNoTargetPressed"
                        />
                        <KButton
                            id="quiz-delete"
                            :label="$t('quiz.deleteBtn')"
                            type="submit"
                            secondary
                            :loading="loadingDeleteBtn"
                            @click="deleteAction"
                        />
                    </div>
                </div>
            </template>
        </KDialog>
    </div>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue';
import { itemsPerPageOptions } from '@/hooks/TableSettingsHooks';
import QuizApi from '@/api/v1/QuizApi';
import { toast } from 'vue3-toastify';
import { navigateTo } from '@/hooks/NavigateHooks';
import { useDataFormat } from '@/hooks/DataHooks';
import variables from '@/assets/scss/modules/variables.module.scss';
import type { ITableHeader, ITableOptions } from '@/models/TableSettingsModel';
import type { IQuizResponse } from '@/models/QuizModel';
import KDialog from '@/components/atoms/KDialog.vue';
import KIcon from '@/components/atoms/KIcon.vue';
import KTooltip from '@/components/atoms/KTooltip.vue';
import KButton from '@/components/atoms/KButton.vue';
import KTextField from '@/components/atoms/KTextField.vue';

const { grey400 } = variables;

const headers = ref<ITableHeader[]>([
    {
        title: t('quiz.name'),
        align: 'start',
        sortable: false,
        key: 'name',
        minWidth: '200px',
    },
    {
        title: t('quiz.createdAt'),
        align: 'center',
        sortable: false,
        key: 'created_at',
        width: '',
        minWidth: '120px',
    },
    {
        title: t('quiz.activeFrom'),
        align: 'center',
        sortable: false,
        key: 'date_from',
        width: '',
        minWidth: '120px',
    },
    {
        title: t('quiz.activeTo'),
        align: 'center',
        sortable: false,
        key: 'date_to',
        width: '',
        minWidth: '120px',
    },
    {
        title: t('quiz.price'),
        align: 'center',
        sortable: false,
        key: 'price',
        width: '',
        minWidth: '150px',
    },
    {
        title: t('quiz.survey'),
        align: 'center',
        sortable: false,
        key: 'survey',
        width: '60px',
        minWidth: '60px',
    },
    {
        title: t('quiz.answers'),
        align: 'center',
        sortable: false,
        key: 'answers',
        width: '',
    },
    {
        title: '',
        align: 'center',
        sortable: false,
        key: 'actions',
        width: '150px',
    },
]);
const loading = ref<boolean>(false);
const currentPage = ref<number>(0);
const lastPage = ref<number>(0);
const total = ref<number>(0);
const items = ref<IQuizResponse[]>([]);
const quizApi = new QuizApi();
const quizList = async() => {
    loading.value = true;
    try {
        const result = await quizApi.quizList(
            options.value.page,
            options.value.itemsPerPage,
            options.value.sortBy.length > 0 ? options.value.sortBy[0].key : null,
            options.value.sortBy.length > 0 ? options.value.sortBy[0].order : null,
            search.value,
        );
        if (!result.isSuccess) {
            toast.error(t('quiz.toast.errorQuizList'));
            loading.value = false;
            return;
        }
        currentPage.value = result.payload.current_page;
        lastPage.value = result.payload.last_page;
        total.value = result.payload.total;
        items.value = result.payload.data;
    } catch (e) {
        toast.error(t('quiz.toast.errorQuizList'));
        loading.value = false;
        return;
    }
    loading.value = false;
};
const options = ref<ITableOptions>({
    page: 1,
    itemsPerPage: 10,
    sortBy: [],
    groupBy: [],
    search: null,
});
const optionsUpdate = (val:ITableOptions) => {
    options.value.page = val.page;
    options.value.itemsPerPage = val.itemsPerPage;
    options.value.search = val.search;
    options.value.groupBy = val.groupBy;
    options.value.sortBy = val.sortBy;
    quizList();
};

const isOpenDeleteDialog = ref<boolean>(false);
const isCloseDeleteDialog = ref<boolean>(true);
const selectedQuizId = ref<number | null>(null);
const closeDialogWhenNoTargetPressed = () => {
    isOpenDeleteDialog.value = false;
    isCloseDeleteDialog.value = true;
    selectedQuizId.value = null;
};
const openDeleteWarehouseDialog = (id: number) => {
    selectedQuizId.value = id;
    isOpenDeleteDialog.value = true;
    isCloseDeleteDialog.value = false;
};
const loadingDeleteBtn = ref<boolean>(false);
const deleteAction = async() => {
    loadingDeleteBtn.value = true;
    try {
        const result = await quizApi.deleteQuiz(selectedQuizId.value);
        if (!result.isSuccess) {
            toast.error(t('quiz.toast.errorQuizDeleteAction'));
            loadingDeleteBtn.value = false;
            closeDialogWhenNoTargetPressed();
            return;
        }
        toast.success(t('quiz.toast.successQuizDeleteAction'));
        closeDialogWhenNoTargetPressed();
        quizList();
    } catch (e) {
        toast.error(t('quiz.toast.errorQuizDeleteAction'));
        loadingDeleteBtn.value = false;
        closeDialogWhenNoTargetPressed();
        return;
    }
    loadingDeleteBtn.value = false;
};

const onRowClick = (event: any, item: any) => {
    navigateTo(`/quiz/${item.item.id}`);
};

const search = ref<string | null>(null);
const searchInterval = ref<any>(undefined);
watch(() => search.value, (newValue, oldValue) => {
    if (newValue !== oldValue) {
        clearTimeout(searchInterval.value);
    }
    searchInterval.value = setTimeout(() => {
        quizList();
    }, 700);
}, {
    deep: true
});
</script>

<style scoped lang="scss">
.admin-quiz-table {
    margin-top: 20px;

    &__loading {
        opacity: 0.6;
    }

    &__action-wrapper {
        display: flex;
        justify-content: space-around;
    }

    &__icon-wrapper {
        padding: 5px;
        height: 26px;
    }

    &__btn-dialog-wrapper {
        margin-top: 10px;
        display: flex;
        flex-direction: column;
        gap: 10px;

        @include media-breakpoint-up(sm) {
            margin-top: 20px;
            flex-direction: row;
            width: 100%;
            justify-content: space-around;
        }
    }

    &__dialog-title {
        font-size: 18px;
        letter-spacing: 0;
    }

    &:deep(.v-data-table tr:hover) {
        .admin-quiz-table__icon-wrapper--edit svg {
            fill: $primary-800;
        }

        .admin-quiz-table__icon-wrapper--trash svg {
            fill: $red-400;
        }
    }
}
</style>
